/**
 * Load every page of a cursor-paginated list.
 */

import { type QueryKey, useInfiniteQuery } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"

interface Page<T> {
  items: T[]
  nextToken?: string
}

/**
 * Fetch every page of a list in the background. Return the flattened items, the first page (for
 * list-level fields), and the pending and error state. `fetchPage` gets the previous `nextToken`.
 */
export function useAllPages<T, L extends object = object>(
  queryKey: QueryKey,
  fetchPage: (nextToken: string | undefined) => Promise<Page<T> & L>,
) {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextToken ?? null,
  })
  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = query

  // Depend on `data` so loading continues when a page resolves without the flags changing.
  useEffect(() => {
    if (data && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [data, hasNextPage, isFetchingNextPage, fetchNextPage])

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data])
  return { items, firstPage: data?.pages[0], isPending: query.isPending, error: query.error }
}
